import React, { useEffect, useReducer } from "react"
import Modal from "react-modal"
import styled from "styled-components"
import { Normalize } from "styled-normalize"
import { WebLNProvider } from "webln"

import {
  isSocketAPI,
  isSocketPlaygroundState,
  RestAPI,
  RestPlaygroundState,
  SocketAPI,
  SocketPlaygroundState
} from "../reducer/common.types"
import { Actions, initialState, reducer, SbDispatch } from "../reducer/reducer"
import { Button, deviceSizes, RootContainer } from "../styles"
import { RequestInvoice, RequestWebLn, WebLNLocalStorage } from "../webln"
import { Header } from "./header"
import { IncomingMessages } from "./incoming.messages"
import { InvoiceQr } from "./invoice.qr"
import {
  MakeRequests,
  MakeRequestsProps,
  MakeRequestsRestProps,
  MakeRequestsSocketProps
} from "./make.requests"

const ModalChild = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const ModalButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`

interface GetSocketPropsArgs {
  state: SocketPlaygroundState<SocketAPI>
  dispatch: SbDispatch
}

/**
 * Calculates the needed props for `MakeRequests` in a WebSocket
 * API scenario
 */
const getSocketProps: (args: GetSocketPropsArgs) => MakeRequestsSocketProps = ({
  dispatch,
  state
}) => ({
  nameMeBetterToBeSent: state.currentMessageToBeSent,
  dispatch,
  api: state.selectedAPI,
  // pending request only applicable for REST based APIs
  pendingRequest: false,
  refundInvoice: state.refundInvoice,
  connected: state.connection === "connected",
  editorState: state.editorState,
  socket: state.websocket
})

interface GetRestPropsArgs {
  state: RestPlaygroundState<RestAPI>
  dispatch: SbDispatch
}

/**
 * Calculates the needed props for `MakeRequests` in a REST
 * API scenario
 */
const getRestProps: (args: GetRestPropsArgs) => MakeRequestsRestProps = ({
  dispatch,
  state
}) => ({
  dispatch,
  api: "historical",
  editorState: state.editorState,
  pendingRequest: state.pendingRequest,
  webLn: state.webLn
})

const App: React.SFC = () => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const webLnEffect = () => {
    const webLn: WebLNProvider | undefined = (window as any).webln

    // user has a webLN provider installed
    if (webLn) {
      // tslint:disable-next-line: no-console
      console.log("User has WebLN installed")

      // we have asked the user for approval before
      if (WebLNLocalStorage.userHasBeenAsked()) {
        dispatch({ type: "SET_WEBLN", webLn })

        if (WebLNLocalStorage.userHasApproved()) {
          // this would throw if user hasn't previously approved
          // tslint:disable-next-line: no-console
          console.log("Enabling WebLN")
          webLn.enable()
        }
      }
      // we have not asked for approval
      else {
        // tslint:disable-next-line: no-console
        console.log("Opening WebLN info modal")
        dispatch({ type: "OPEN_WEBLN_INFO_MODAL" })
      }
    }
    // user lacks WebLN provider
    else {
      // tslint:disable-next-line: no-console
      console.log("User lacks WebLN")
    }
  }

  useEffect(webLnEffect, [])

  const makeRequestsProps: MakeRequestsProps = isSocketPlaygroundState(state)
    ? getSocketProps({ dispatch, state })
    : getRestProps({ dispatch, state })

  const isMobile = window.innerWidth <= deviceSizes.tabletS
  return (
    <>
      <Normalize />
      <Modal
        style={{
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            maxWidth: "50%",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)"
          }
        }}
        isOpen={state.isWebLnModalOpen && !isMobile}
      >
        <ModalChild>
          <p>
            When subscribing to our streaming APIs, you can request a refund at
            any time by cancelling your subscription. To facilitate this, we
            need an invoice from you without an amount specifed. If you enable
            WebLN (like for example Lightning Joule or BlueWallet) we can
            automatically generate an invoice for you.
          </p>
          <p>
            We can also pay invoices for you when requesting data over our REST
            APIs, and then automatically decrypt them with the payment preimage.
          </p>
          <ModalButtonWrapper>
            <Button
              onClick={() => {
                dispatch({ type: "CLOSE_WEBLN_INFO_MODAL" })
                RequestWebLn(dispatch)
              }}
            >
              OK
            </Button>
          </ModalButtonWrapper>
        </ModalChild>
      </Modal>
      <RootContainer>
        <Header
          onConnectionButtonClick={() => {
            const { hasAskedForInvoice, webLn, selectedAPI } = state

            if (isSocketAPI(selectedAPI) && !hasAskedForInvoice && webLn) {
              RequestInvoice({ dispatch, ln: webLn })
            }

            if (isSocketPlaygroundState(state)) {
              Actions.onConnectButtonClick(state, dispatch)
            }
          }}
          onAPIChange={event =>
            Actions.onAPIChange({
              dispatch,
              event,
              selectedAPI: state.selectedAPI
            })
          }
          state={state}
        />
        <MakeRequests {...makeRequestsProps} />
        <InvoiceQr webLN={state.webLn} invoice={state.invoice} />
        <IncomingMessages
          api={state.selectedAPI}
          clearMessages={() => {
            dispatch({ type: "CLEAR_MESSAGES" })
            dispatch({ type: "CLEAR_INVOICE" })
          }}
          messages={state.messages}
        />
      </RootContainer>
    </>
  )
}

export default App
